import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { unwrap } from '../../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import MaintenanceAgentSettingsCard, {
  MAINTENANCE_SETTINGS_KEY,
  useMaintenanceAgentSettings as useMaintenanceSettings,
} from '../../components/equipment/MaintenanceAgentSettingsCard';
import { monthLabel } from '../../lib/billing-agent';
import {
  MAINTENANCE_MODES,
  MAINTENANCE_MODE_COPY,
  SUGGEST_AVAILABLE,
  SUGGEST_LOCKED_NOTE,
  countOfPhrase,
  countPhrase,
  currentMetricMonth,
  maintenanceFlagTypeLabel,
  orderFlagTypes,
  pctPhrase,
  ratePhrase,
  shadowPendingSentence,
} from '../../lib/maintenance-agent';
import type { MaintenanceAgentMode } from '../../types/maintenance-flags';
import type { MaintenanceAgentMetrics, MaintenanceShadowReport } from '../../types/maintenance-agent';

/**
 * Settings → AI & integrations → Maintenance agent (maintenance-agent brief
 * FE-4; api#944 settings, api#1012 metrics and shadow report). Laid out as the
 * Billing agent tab is, so the two read as a set.
 *
 *   1. Mode — off / shadow / suggest. Anyone who can read it sees it; only an
 *      `admin` may change it, because the PUT is STRICT admin. Suggest is shown
 *      and not offered yet (SUGGEST_AVAILABLE).
 *   1b. Thresholds — left-on-site days and whether inspections are tracked at
 *      all (moved here from Equipment Types, as FE-5 planned).
 *   2. What it saved this month — the five figures, for the TENANT-LOCAL month.
 *   3. Shadow — only while the mode is `shadow`: per type, how often the office
 *      cleared the condition on its own, and how many flags are still pending.
 *
 * Every figure is the API's. Nothing here adds, divides or averages one.
 *
 * The AI & integrations workspace renders the heading and the tab strip; this
 * page owns neither a heading nor a back arrow.
 */

const METRICS_KEY = ['maintenance-agent', 'metrics'] as const;
const SHADOW_REPORT_KEY = ['maintenance-agent', 'shadow-report'] as const;
const SHADOW_REPORT_DAYS = 30;

const PANEL = 'rounded-fi-panel border border-fieldiq-border bg-fieldiq-card';
const EYEBROW = 'text-[11px] font-semibold uppercase tracking-wide text-fieldiq-muted';

function Pulse({ label }: { label: string }) {
  return <span role="status" aria-label={label} className="inline-block h-4 w-20 animate-pulse rounded bg-fieldiq-border" />;
}

const isMode = (v: unknown): v is MaintenanceAgentMode =>
  typeof v === 'string' && (MAINTENANCE_MODES as string[]).includes(v);

// ─── 1. Mode ─────────────────────────────────────────────────────────────────

function ModeSection() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  // Strict admin, as the API's PUT is. isAdminRole would admit an owner the API refuses.
  const canChange = user?.role === 'admin';
  const query = useMaintenanceSettings();
  const failure = queryLoadFailure(query);
  const raw = query.data?.mode;
  const current = isMode(raw) ? raw : null;

  const mutation = useMutation({
    mutationFn: (mode: MaintenanceAgentMode) => api.maintenanceAgent.updateSettings({ mode }),
    onSuccess: (_res, mode) => toast.success(`Maintenance agent set to ${MAINTENANCE_MODE_COPY[mode].label}`),
    onError: (err: any) => toast.error(err?.response?.data?.error || 'Failed to change the maintenance agent mode'),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: MAINTENANCE_SETTINGS_KEY });
      queryClient.invalidateQueries({ queryKey: METRICS_KEY });
      queryClient.invalidateQueries({ queryKey: SHADOW_REPORT_KEY });
      // Every asset's and job's panel reads the mode off its own payload.
      queryClient.invalidateQueries({ queryKey: ['maintenance-flags'] });
    },
  });

  const offered = (mode: MaintenanceAgentMode) => mode !== 'suggest' || SUGGEST_AVAILABLE;
  const choose = (mode: MaintenanceAgentMode) => {
    if (!canChange || !offered(mode) || mode === current || mutation.isPending) return;
    mutation.mutate(mode);
  };

  return (
    <section aria-labelledby="maintenance-agent-mode" className={`${PANEL} border-l-4 border-l-fi-amber`} data-testid="maintenance-agent-mode">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Maintenance agent</p>
        <h2 id="maintenance-agent-mode" className="text-base font-semibold text-white">Mode</h2>
      </div>
      <div className="px-4 pb-4">
        {failure ? (
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the maintenance agent's mode" allowed="admins and office staff" />
        ) : query.isLoading || !query.data ? (
          <Pulse label="Loading the mode" />
        ) : !current ? (
          // A mode this build does not know: claim none of the three.
          <p className="text-sm text-fieldiq-text" data-testid="mode-unknown">—</p>
        ) : (
          <div role="radiogroup" aria-label="Maintenance agent mode" aria-readonly={!canChange} className="grid gap-2 md:grid-cols-3">
            {MAINTENANCE_MODES.map((mode) => {
              const selected = mode === current;
              const locked = !offered(mode);
              return (
                <button
                  key={mode}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={!canChange || locked || mutation.isPending}
                  onClick={() => choose(mode)}
                  data-testid={`mode-${mode}`}
                  className={`text-left rounded-lg border p-3 transition-colors ${
                    selected ? 'border-fieldiq-accent bg-fieldiq-dark' : 'border-fieldiq-border'
                  } ${canChange && !locked ? 'hover:border-fieldiq-accent' : 'cursor-default'} disabled:opacity-100`}
                >
                  <span className="flex items-center gap-2 text-sm font-semibold text-white">
                    {selected && <Check size={14} aria-hidden className="text-fieldiq-accent" />}
                    {MAINTENANCE_MODE_COPY[mode].label}
                  </span>
                  <span className="mt-1 block text-xs text-fieldiq-muted">{MAINTENANCE_MODE_COPY[mode].sentence}</span>
                  {locked && (
                    <span className="mt-2 flex items-center gap-1 text-xs font-semibold text-fieldiq-text" data-testid="suggest-locked">
                      <Lock size={12} aria-hidden className="text-fieldiq-muted" />
                      {SUGGEST_LOCKED_NOTE}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {!canChange && !failure && (
        <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted" data-testid="mode-read-only">Only an admin can change this.</p>
      )}
    </section>
  );
}

// ─── 2. What it saved this month ─────────────────────────────────────────────

/**
 * One row per figure. The definitions are the API's (the docblock of
 * `services/maintenance-watch-metrics.service.ts`), shortened, not invented.
 */
function figureRows(m: MaintenanceAgentMetrics) {
  const fr = m.flagsRaised;
  const a = m.accepted;
  const cs = m.caughtBeforeUse?.suggest;
  const ch = m.caughtBeforeUse?.shadow;
  const u = m.usageRecorded;
  const d = m.inspectionDisagreements;
  const unknown = (cs?.unknownStart ?? 0) + (ch?.unknownStart ?? 0);
  return [
    {
      key: 'flags-raised',
      label: 'Flags raised',
      value: countPhrase(fr?.total),
      sub: fr ? `${countPhrase(fr.shadow)} in shadow, ${countPhrase(fr.suggest)} shown to the office` : null,
      definition: 'Flags the agent raised this month. A flag it updated later counts once, in the month it was first raised.',
    },
    {
      key: 'accepted',
      label: 'Accepted',
      value: ratePhrase(a?.resolved, a?.answered, a?.acceptedPct),
      sub: null,
      definition: 'Of the flags the office answered this month, the share it resolved rather than dismissed. Shadow flags are never shown, so never answered.',
    },
    {
      key: 'caught',
      label: 'Caught before use',
      value: countOfPhrase(cs?.caught, cs?.considered),
      sub: ch ? `Without the agent (shadow): ${countOfPhrase(ch.caught, ch.considered)}` : null,
      note: unknown > 0 ? `${unknown} on jobs with no recorded start time, counted as neither` : null,
      definition: "Flags on a job before it started where the asset was taken off the job or inspected before the job's start.",
    },
    {
      key: 'usage',
      label: 'Usage recorded',
      value: ratePhrase(u?.recorded, u?.pairs, u?.recordedPct),
      sub: null,
      definition: "Equipment assigned to a job with an approved ticket that appears on one of that job's tickets, by the month of the job's first approved ticket.",
    },
    {
      key: 'disagreements',
      label: 'Inspection disagreements',
      value: countOfPhrase(d?.disagreements, d?.submissions),
      sub: null,
      definition: "Inspections submitted this month where the crew's overall result differs from the result worked out from its items.",
    },
  ];
}

function MetricsSection({ month }: { month: string | null }) {
  const timeZone = useTenantTimeZone();
  const query = useQuery({
    queryKey: [...METRICS_KEY, month],
    queryFn: async () => unwrap<MaintenanceAgentMetrics>((await api.maintenanceAgent.metrics(month!)).data),
    enabled: !!month,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const loading = !failure && (!query.data || query.isLoading);

  return (
    <section aria-labelledby="maintenance-agent-metrics" className={`${PANEL} border-l-4 border-l-fi-amber`} data-testid="maintenance-agent-metrics">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>{month ? monthLabel(month) : 'This month'}</p>
        <h2 id="maintenance-agent-metrics" className="text-base font-semibold text-white">What it saved this month</h2>
      </div>
      {failure ? (
        <div className="px-4 pb-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the maintenance agent's metrics" allowed="admins and office staff" />
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px] text-sm">
              <thead>
                <tr className="border-b border-fieldiq-border text-left text-xs text-fieldiq-muted">
                  <th scope="col" className="px-3 py-2 font-medium">Figure</th>
                  <th scope="col" className="px-3 py-2 font-medium">{month ? monthLabel(month) : 'This month'}</th>
                </tr>
              </thead>
              <tbody>
                {loading || !query.data
                  ? ['Flags raised', 'Accepted', 'Caught before use', 'Usage recorded', 'Inspection disagreements'].map((label) => (
                    <tr key={label} className="border-b border-fieldiq-border last:border-b-0">
                      <th scope="row" className="px-3 py-2 text-left font-medium text-fieldiq-text">{label}</th>
                      <td className="px-3 py-2"><Pulse label="Loading" /></td>
                    </tr>
                  ))
                  : figureRows(query.data).map((r) => (
                    <tr key={r.key} className="border-b border-fieldiq-border last:border-b-0 align-top" data-testid={`metric-${r.key}`}>
                      <th scope="row" className="px-3 py-2 text-left font-medium text-fieldiq-text">
                        {r.label}
                        <span className="mt-0.5 block text-xs font-normal text-fieldiq-muted">{r.definition}</span>
                      </th>
                      <td className="px-3 py-2">
                        <span className="tabular-nums text-fieldiq-text" data-testid={`metric-${r.key}-value`}>{r.value}</span>
                        {r.sub && <span className="block text-xs text-fieldiq-muted">{r.sub}</span>}
                        {'note' in r && r.note && <span className="block text-xs text-fieldiq-muted">{r.note}</span>}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
            A calendar month in the company's time zone{timeZone ? ` (${timeZone})` : ''}.
          </p>
        </>
      )}
    </section>
  );
}

// ─── 3. Shadow ───────────────────────────────────────────────────────────────

function ShadowSection() {
  const query = useQuery({
    queryKey: [...SHADOW_REPORT_KEY, SHADOW_REPORT_DAYS],
    queryFn: async () => unwrap<MaintenanceShadowReport>((await api.maintenanceAgent.shadowReport(SHADOW_REPORT_DAYS)).data),
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
  const failure = queryLoadFailure(query);
  const r = query.data;
  const pending = r ? shadowPendingSentence(r.pending, r.windowDays) : null;
  const types = r ? orderFlagTypes(Object.keys(r.byType ?? {})) : [];

  return (
    <section aria-labelledby="maintenance-agent-shadow" className={PANEL} data-testid="maintenance-agent-shadow">
      <div className="px-4 pt-4 pb-2">
        <p className={EYEBROW}>Shadow, last {SHADOW_REPORT_DAYS} days</p>
        <h2 id="maintenance-agent-shadow" className="text-base font-semibold text-white">
          Office cleared the condition without the agent
        </h2>
      </div>
      {failure ? (
        <div className="px-4 pb-4">
          <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="the shadow report" allowed="admins and office staff" />
        </div>
      ) : query.isLoading || !r ? (
        <div className="px-4 pb-4"><Pulse label="Loading the shadow report" /></div>
      ) : r.flags === 0 ? (
        <p className="px-4 pb-4 text-sm text-fieldiq-text" data-testid="shadow-none">
          The agent raised no shadow flags in the last {SHADOW_REPORT_DAYS} days.
        </p>
      ) : (
        <>
          <p className="px-4 pb-2 text-sm text-fieldiq-text tabular-nums" data-testid="shadow-summary">
            {r.flags} {r.flags === 1 ? 'flag' : 'flags'}: {r.acted} acted on, {r.notActed} not acted on, {r.pending} pending.
            Share acted on: {pctPhrase(r.actedPct)}.
          </p>
          {pending && (
            <p role="note" data-testid="shadow-pending"
              className="mx-4 mb-3 rounded-lg border border-fieldiq-border bg-fieldiq-dark px-3 py-2 text-sm text-fieldiq-text">
              {pending}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="w-full min-w-[640px] text-sm" aria-label="Shadow flags by check">
              <thead>
                <tr className="border-b border-fieldiq-border text-left text-xs text-fieldiq-muted">
                  <th scope="col" className="px-3 py-2 font-medium">Check</th>
                  <th scope="col" className="px-3 py-2 font-medium text-right">Flags</th>
                  <th scope="col" className="px-3 py-2 font-medium text-right">Acted on</th>
                  <th scope="col" className="px-3 py-2 font-medium text-right">Not acted on</th>
                  <th scope="col" className="px-3 py-2 font-medium text-right">Pending</th>
                  <th scope="col" className="px-3 py-2 font-medium text-right">Share acted on</th>
                </tr>
              </thead>
              <tbody>
                {types.map((t) => {
                  const f = r.byType[t];
                  return (
                    <tr key={t} className="border-b border-fieldiq-border last:border-b-0" data-testid={`shadow-type-${t}`}>
                      <th scope="row" className="px-3 py-2 text-left font-medium text-fieldiq-text">{maintenanceFlagTypeLabel(t)}</th>
                      <td className="px-3 py-2 text-right tabular-nums text-fieldiq-text">{countPhrase(f?.flags)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-fieldiq-text">{countPhrase(f?.acted)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-fieldiq-text">{countPhrase(f?.notActed)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-fieldiq-text">{countPhrase(f?.pending)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-fieldiq-text" data-testid={`shadow-share-${t}`}>{pctPhrase(f?.actedPct)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="border-t border-fieldiq-border px-4 py-2 text-xs text-fieldiq-muted">
            Nobody sees a shadow flag. It counts as acted on when, within {r.windowDays} days, the asset was inspected,
            repaired, sent to maintenance or retired (or brought back, for left on site), or taken off the job.
          </p>
        </>
      )}
    </section>
  );
}

// ─── the page ────────────────────────────────────────────────────────────────

export default function MaintenanceAgentPage() {
  const { user } = useAuth();
  const timeZone = useTenantTimeZone();
  // The tenant's own month (Settings time zone). Null until it is known: no
  // device or Edmonton fallback.
  const month = currentMetricMonth(timeZone);
  // Same key as the Mode section's query, so this makes no second request.
  const settings = useMaintenanceSettings();
  const showShadow = settings.data?.mode === 'shadow';

  return (
    <div className="min-h-full">
      <div className="px-6 py-4 space-y-4 pb-[var(--bottom-chrome)]">
        <p className="text-xs text-fieldiq-muted">
          Every day the agent checks equipment against its inspections, certifications, service schedules and usage,
          and raises flags the office answers. It never changes equipment or a job.
        </p>
        <ModeSection />
        <MaintenanceAgentSettingsCard isAdmin={user?.role === 'admin'} />
        <MetricsSection month={month} />
        {showShadow && <ShadowSection />}
      </div>
    </div>
  );
}
